import React from 'react';

import Button from '../_custom/Button/Button';
import Modal from '../_custom/Modal/Modal';
import { PURPLE_WARNING_CIRCLE } from '../../assets/hostedassets';
import modalOverDrawerStyles from '../ConfirmDialog/modalOverDrawer.module.scss';

import styles from './UnsavedEditsDialog.module.scss';

export interface UnsavedEditsDialogProps {
    open: boolean;
    /** Keep the editor open with the edits intact. */
    onKeepEditing: () => void;
    /** Close the editor and drop the edits. */
    onDiscard: () => void;
    /** Add the edited file to Workspace, then close. Absent when the drawer has
     *  no add action (e.g. a citation preview) — the dialog then offers only
     *  keep/discard. */
    onAddToWorkspace?: () => void;
    /** An add is already in flight (started from the header): the Add button waits for it. */
    adding?: boolean;
}

/**
 * Close guard for the editable file drawers (SheetEditorDrawer / DocxDrawer):
 * shown when the user tries to close with edits that haven't reached
 * Workspace. Deliberately NOT `ConfirmationPopup` — that one treats a backdrop
 * click as its cancel action, which here would mean a misclick silently
 * discarding the user's working. This modal is `persistent`, so the only
 * ways out are the three explicit choices. Renders nothing while closed:
 * `Modal` keeps closed content in the DOM, which would leave a second, hidden
 * "Add to Workspace" button next to the drawer header's own.
 */
const UnsavedEditsDialog: React.FC<UnsavedEditsDialogProps> = ({ open, onKeepEditing, onDiscard, onAddToWorkspace, adding = false }) =>
    !open ? null : (
        <Modal
            open={open}
            onClose={onKeepEditing}
            size="flexible"
            // Above the drawer it guards: at the Modal layer it opened behind the
            // full-screen drawer, so × after an edit looked like it did nothing.
            className={modalOverDrawerStyles.modalOverDrawer}
            persistent
            withoutCloseButton
            style={{ padding: 0, borderRadius: '0.75rem' }}
        >
            <div className={styles.UnsavedEditsDialog} role="alertdialog" aria-labelledby="unsaved-edits-title">
                <div className={styles.WarningIcon}>
                    <img src={PURPLE_WARNING_CIRCLE} alt="" aria-hidden />
                </div>
                <div className={styles.Text}>
                    <h3 id="unsaved-edits-title" className={styles.Title}>
                        Unsaved changes
                    </h3>
                    <p className={styles.SubTitle}>
                        {onAddToWorkspace
                            ? 'Your edits have not been added to Workspace yet. Add them now, or discard them?'
                            : 'Your edits have not been saved and will be lost if you close.'}
                    </p>
                </div>
                <div className={styles.CtaWrap}>
                    <Button text="Discard" onClick={onDiscard} variant="purpleinverted" style={{ marginTop: '0' }} />
                    {onAddToWorkspace ? (
                        <>
                            <Button text="Keep editing" onClick={onKeepEditing} variant="purpleinverted" style={{ marginTop: '0' }} />
                            <Button
                                text="Add to Workspace"
                                onClick={onAddToWorkspace}
                                variant="purple"
                                className={styles.PrimaryCta}
                                style={{ marginTop: '0' }}
                                disabled={adding}
                                loading={adding}
                            />
                        </>
                    ) : (
                        <Button text="Keep editing" onClick={onKeepEditing} variant="purple" style={{ marginTop: '0' }} />
                    )}
                </div>
            </div>
        </Modal>
    );

export default UnsavedEditsDialog;
